// src/pages/DeliveryPartner/Wallet/Wallet.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import Header from "../../../components/layout/Header/Header";
import Sidebar from "../../../components/layout/Sidebar/Sidebar";
import DirhamSymbol from "../../../Assets/aed-symbol.png";
import toast from "../../../utils/toast";
import axios from "axios";
import "./Wallet.css";
import { useAppSettings } from "../../../context/AppSettingsContext";
import { usePaginatedData } from "../../../hooks/usePagination";
import Pagination from "../../../components/common/Pagination";
import TableScrollSync from "../../../components/common/TableScrollSync";

// Icons (unchanged)
const CashIcon = () => (
  <svg
    className="icon-cash"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <rect x="2" y="6" width="20" height="12" rx="2" />
    <circle cx="12" cy="12" r="3" />
    <path d="M6 12h.01M18 12h.01" />
  </svg>
);

const SearchIcon = () => (
  <svg
    className="icon-search"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const PrintIcon = () => (
  <svg
    className="icon-print"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M6 9V2h12v7" />
    <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" />
    <rect x="6" y="14" width="12" height="8" rx="1" />
  </svg>
);

const SendIcon = () => (
  <svg
    className="icon-send"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M22 2L11 13" />
    <path d="M22 2L15 22L11 13L2 9L22 2Z" />
  </svg>
);

const Wallet = () => {
  const [walletData, setWalletData] = useState({
    totalAmount: 0,
    transactions: [],
  });
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeItem, setActiveItem] = useState("Cash Wallet");
  const [user, setUser] = useState(null);
  const [payingTxId, setPayingTxId] = useState(null);
  const [printingTxId, setPrintingTxId] = useState(null);

  // Selection
  const [selectedTxIds, setSelectedTxIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const [searchCustomer, setSearchCustomer] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [txToConfirm, setTxToConfirm] = useState(null);
  const [isBulkRequest, setIsBulkRequest] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_IP;

  const fetchCurrentUser = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return (window.location.href = "/login");
      const res = await axios.get(`${backendUrl}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data.user || res.data);
    } catch (err) {
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
  }, [backendUrl]);

  const fetchCashWallet = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${backendUrl}/api/wallet/delivery/cash-wallet`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setWalletData(res.data);
      setSelectedTxIds([]);
      setSelectAll(false);
    } catch (err) {
      toast.error("Failed to load cash wallet");
    } finally {
      setLoading(false);
    }
  }, [backendUrl]);

  useEffect(() => {
    fetchCurrentUser();
    fetchCashWallet();
  }, [fetchCurrentUser, fetchCashWallet]);

  const { invoiceSystemActive } = useAppSettings();
  // A transaction "has" an invoice once its order carries one — once
  // InvoiceCounter is deleted those rows are dropped entirely.
  const txHasInvoice = (tx) =>
    !!tx.displayInvoiceNumber || !!tx.order?.invoiceNumber || !!tx.invoiceNumber;

  // Filtered + totals
  const filteredTransactions = useMemo(() => {
    let list = invoiceSystemActive
      ? walletData.transactions
      : walletData.transactions.filter((tx) => !txHasInvoice(tx));
    if (!searchCustomer.trim()) return list;
    const term = searchCustomer.toLowerCase();
    return list.filter((tx) =>
      tx.order?.customer?.name?.toLowerCase().includes(term),
    );
  }, [walletData.transactions, searchCustomer, invoiceSystemActive]);

  const filteredTotal = useMemo(
    () => filteredTransactions.reduce((sum, tx) => sum + tx.amount, 0),
    [filteredTransactions],
  );

  const selectedTotal = useMemo(
    () =>
      walletData.transactions
        .filter((tx) => selectedTxIds.includes(tx._id))
        .reduce((sum, tx) => sum + tx.amount, 0),
    [walletData.transactions, selectedTxIds],
  );

  // "Total Cash Collected" comes straight from the backend for the whole
  // dataset — once invoiced rows are hidden, recompute it from what's
  // actually visible so the summary card matches the table.
  const visibleTotalAmount = invoiceSystemActive
    ? walletData.totalAmount
    : walletData.transactions
        .filter((tx) => !txHasInvoice(tx))
        .reduce((sum, tx) => sum + tx.amount, 0);

  const { entriesPerPage } = useAppSettings();
  const pagination = usePaginatedData(filteredTransactions, entriesPerPage, `${searchCustomer}`);

  // Selection handlers
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedTxIds([]);
    } else {
      setSelectedTxIds(filteredTransactions.map((tx) => tx._id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectTx = (id) => {
    setSelectedTxIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  // Request Pay to Admin
  const handleRequestPayToAdmin = (txId = null, isBulk = false) => {
    setIsBulkRequest(isBulk);
    setTxToConfirm(txId);
    setShowConfirmModal(true);
  };

  const confirmRequestPayToAdmin = async () => {
    const ids = isBulkRequest ? selectedTxIds : [txToConfirm];
    if (ids.length === 0) return;

    setShowConfirmModal(false);
    setPayingTxId(isBulkRequest ? "bulk" : txToConfirm);

    try {
      const token = localStorage.getItem("token");
      for (const id of ids) {
        await axios.post(
          `${backendUrl}/api/wallet/delivery/request-pay-cash-to-admin`,
          { transactionId: id },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }
      toast.success(
        isBulkRequest ? "Bulk requests sent!" : "Request sent to admin!",
      );
      fetchCashWallet();
      setSelectedTxIds([]);
    } catch (err) {
      toast.error("Failed to send request");
    } finally {
      setPayingTxId(null);
      setTxToConfirm(null);
      setIsBulkRequest(false);
    }
  };

  // Print Receipt (with single receipt number for same customer/day)
 const handlePrintReceipt = async (txId = null, isBulk = false) => {
  const ids = isBulk ? selectedTxIds : [txId];
  if (ids.length === 0) return;

  setPrintingTxId(isBulk ? "bulk" : txId);

  try {
    const token = localStorage.getItem("token");
    const selectedTxs = walletData.transactions.filter((t) =>
      ids.includes(t._id),
    );

    // ✅ Get correct invoice number for filename using displayInvoiceNumber
    const firstTx = selectedTxs[0];
    const firstInvoiceNumber = 
      firstTx.displayInvoiceNumber || 
      firstTx.invoiceNumber || 
      firstTx.order?.invoiceNumber || 
      "N/A";

    const res = await axios.post(
      `${backendUrl}/api/wallet/receipt/bulk`,
      {
        transactionIds: ids,
        // ✅ Pass invoice numbers array so backend can use them
        invoiceNumbers: selectedTxs.map(tx => 
          tx.displayInvoiceNumber || tx.invoiceNumber || tx.order?.invoiceNumber
        ),
      },
      {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      },
    );

    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);

    // ✅ Use correct invoice number in filename
    const filename = selectedTxs.length === 1
      ? `receipt-${firstInvoiceNumber}.pdf`
      : `bulk-receipt-${Date.now()}.pdf`;

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    toast.success("Receipt ready!");
    if (isBulk) setSelectedTxIds([]);
  } catch (err) {
    toast.error("Failed to generate receipt");
  } finally {
    setPrintingTxId(null);
  }
};

const downloadSingleReceipt = async (txId, invoiceNumber) => {
  try {
    const token = localStorage.getItem("token");
    const response = await fetch(`${backendUrl}/api/wallet/receipt/${txId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    
    if (!response.ok) throw new Error("Failed to generate receipt");
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `receipt-${invoiceNumber}.pdf`;
    link.click();
    window.URL.revokeObjectURL(url);
    toast.success("Receipt downloaded");
  } catch (err) {
    toast.error("Failed to download receipt");
  }
};

  if (!user) return <div className="wallet-loading">Loading...</div>;

  return (
    <div className="wallet-layout">
      <Header
        sidebarOpen={sidebarOpen}
        onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        user={user}
      />
      <Sidebar
        isOpen={sidebarOpen}
        activeItem={activeItem}
        onSetActiveItem={setActiveItem}
        onClose={() => setSidebarOpen(false)}
        user={user}
      />

      <main
        className={`wallet-main-content ${sidebarOpen ? "sidebar-open" : ""}`}
      >
        <div className="wallet-container-wrapper">
          <div className="wallet-container">
            <div className="wallet-header">
              <div className="wallet-header-content">
                <div className="wallet-header-icon">
                  <CashIcon />
                </div>
                <div>
                  <h1 className="wallet-page-title">Cash Wallet</h1>
                  <p className="wallet-page-subtitle">
                    Manage your collected cash payments
                  </p>
                </div>
              </div>
              <button
                className="wallet-refresh-btn"
                onClick={fetchCashWallet}
                disabled={loading}
              >
                <span className="wallet-btn-icon">⟳</span>
                {loading ? "Refreshing..." : "Refresh"}
              </button>
            </div>

            <div className="wallet-summary-card">
              <div className="wallet-summary-card-content">
                <div className="wallet-summary-card-icon">
                  <img
                    src={DirhamSymbol}
                    alt="AED"
                    className="wallet-dirham-icon-large"
                  />
                </div>
                <div className="wallet-summary-card-details">
                  <span className="wallet-summary-card-label">
                    Total Cash Collected
                  </span>
                  <span className="wallet-summary-card-amount">
                    AED{" "}
                    {visibleTotalAmount.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                    })}
                  </span>
                </div>
              </div>
              <div className="wallet-summary-card-glow" />
            </div>

            <div className="wallet-search-wrapper">
              <div className="wallet-search-input-group">
                <SearchIcon />
                <input
                  type="text"
                  className="wallet-search-input"
                  placeholder="Search by customer name..."
                  value={searchCustomer}
                  onChange={(e) => setSearchCustomer(e.target.value)}
                />
                {searchCustomer && (
                  <button
                    onClick={() => setSearchCustomer("")}
                    className="wallet-search-clear-btn"
                    title="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>

              {searchCustomer.trim() && (
                <div className="wallet-filtered-info">
                  Showing {filteredTransactions.length} transaction
                  {filteredTransactions.length !== 1 ? "s" : ""} — Total:{" "}
                  <strong>AED {filteredTotal.toFixed(2)}</strong>
                </div>
              )}
            </div>

            {selectedTxIds.length > 0 && (
              <div className="wallet-bulk-actions-bar">
                <span>
                  {selectedTxIds.length} selected — Total:{" "}
                  <strong>AED {selectedTotal.toFixed(2)}</strong>
                </span>
                <div className="wallet-bulk-actions-buttons">
                  <button
                    className="wallet-btn wallet-btn-primary wallet-btn-sm"
                    onClick={() => handleRequestPayToAdmin(null, true)}
                    disabled={payingTxId === "bulk"}
                  >
                    <SendIcon />{" "}
                    {payingTxId === "bulk" ? "Sending..." : "Send to Admin"}
                  </button>
                  <button
                    className="wallet-btn wallet-btn-secondary wallet-btn-sm"
                    onClick={() => handlePrintReceipt(null, true)}
                    disabled={printingTxId === "bulk"}
                  >
                    <PrintIcon />{" "}
                    {printingTxId === "bulk"
                      ? "Generating..."
                      : "Print Receipts"}
                  </button>
                  <button className="wallet-btn wallet-btn-ghost wallet-btn-sm" onClick={() => setSelectedTxIds([])}>
                    Clear
                  </button>
                </div>
              </div>
            )}

            {loading ? (
              <div className="wallet-skeleton cash">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="wallet-skeleton-row">
                    <div className="wallet-skeleton-cell wallet-skeleton-checkbox" />
                    <div className="wallet-skeleton-cell wallet-skeleton-text short" />
                    <div className="wallet-skeleton-cell wallet-skeleton-text" />
                    <div className="wallet-skeleton-cell wallet-skeleton-text short" />
                    <div className="wallet-skeleton-cell wallet-skeleton-text short" />
                    <div className="wallet-skeleton-cell wallet-skeleton-text short" />
                    <div className="wallet-skeleton-cell wallet-skeleton-text short" />
                    <div className="wallet-skeleton-cell wallet-skeleton-badge" />
                    <div className="wallet-skeleton-cell wallet-skeleton-button" />
                  </div>
                ))}
              </div>
            ) : filteredTransactions.length === 0 ? (
              <div className="wallet-empty-state">
                <div className="wallet-empty-state-icon">💵</div>
                <h3>No cash transactions found</h3>
                <p>{searchCustomer ? "Try adjusting your search" : "Cash payments will appear here once received"}</p>
              </div>
            ) : (
              <TableScrollSync>
                <div className="wallet-table-container">
                <table className="wallet-data-table">
                <thead>
                  <tr>
                    <th className="wallet-col-checkbox">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="wallet-checkbox-custom"
                      />
                    </th>
                    <th className="wallet-col-index">No</th>
                    <th className="wallet-col-customer">Customer</th>
                    <th className="wallet-col-order">Invoice #</th>
                    <th className="wallet-col-amount">Amount</th>
                    <th className="wallet-col-return-credit">Return Credit Used</th>
                    <th className="wallet-col-date">Date</th>
                    <th className="wallet-col-status">Status</th>
                    <th className="wallet-col-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagination.pageData.map((tx, idx) => (
                    <tr key={tx._id} className={`wallet-tx-row ${selectedTxIds.includes(tx._id) ? "wallet-tx-row-selected" : ""}`}>
                      <td className="wallet-col-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedTxIds.includes(tx._id)}
                          onChange={() => handleSelectTx(tx._id)}
                          disabled={tx.status !== "received"}
                          className="wallet-checkbox-custom"
                        />
                      </td>
                      <td className="wallet-col-index">{pagination.showingFrom + idx}</td>
                      <td className="wallet-col-customer">
                        <div className="wallet-customer-cell">
                          <div className="wallet-customer-avatar">
                            {tx.order?.customer?.name?.charAt(0)?.toUpperCase() || "?"}
                          </div>
                          <span className="wallet-customer-name">{tx.order?.customer?.name || "N/A"}</span>
                        </div>
                      </td>
                      <td className="wallet-col-order">
  {/* ✅ Use displayInvoiceNumber from backend, fallback to order.invoiceNumber */}
  <code className="wallet-order-id">{tx.displayInvoiceNumber || tx.order?.invoiceNumber || tx.invoiceNumber || "N/A"}</code>
</td>
                      <td className="wallet-col-amount">
                        <div className="wallet-amount-cell">
                          <img src={DirhamSymbol} alt="AED" className="wallet-dirham-icon-small" />
                          <span className="wallet-amount-value">{tx.amount.toFixed(2)}</span>
                        </div>
                      </td>
                      <td className="wallet-col-return-credit">
                        {tx.returnCreditUsed > 0
                          ? <span style={{ color: "#1d4ed8", fontWeight: 600 }}>AED {tx.returnCreditUsed.toFixed(2)}</span>
                          : <span style={{ color: "#9ca3af" }}>—</span>}
                      </td>
                      <td className="wallet-col-date">{new Date(tx.date).toLocaleDateString()}</td>
                      <td className="wallet-col-status">
                        <span className={`wallet-status-badge wallet-status-${tx.status}`}>
                          {tx.status === "received" && <span className="wallet-status-dot pulse" />}
                          {tx.status === "received"
                            ? "Ready to Send"
                            : tx.status === "pending"
                            ? "Sent to Admin"
                            : tx.status === "paid_to_admin"
                            ? "Paid to Admin"
                            : tx.status}
                        </span>
                      </td>
                      <td className="wallet-col-actions">
                        <div className="wallet-row-actions">
                          {tx.status === "received" && (
                            <button
                              className="wallet-btn-action wallet-btn-send"
                              onClick={() => handleRequestPayToAdmin(tx._id)}
                              title="Send to Admin"
                            >
                              <SendIcon />
                            </button>
                          )}
                          <button
                            className="wallet-btn-action wallet-btn-print"
                            onClick={() => downloadSingleReceipt(tx._id, tx.displayInvoiceNumber || tx.order?.invoiceNumber)}
                            title="Print Receipt"
                          >
                            <PrintIcon />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                </table>
                </div>
              </TableScrollSync>
            )}
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              totalRecords={pagination.totalRecords}
              showingFrom={pagination.showingFrom}
              showingTo={pagination.showingTo}
              canPrev={pagination.canPrev}
              canNext={pagination.canNext}
              onPrev={pagination.goPrev}
              onNext={pagination.goNext}
            />
          </div>
        </div>
      </main>

      {/* Confirm Modal */}
      {showConfirmModal && (
        <div className="wallet-modal-backdrop">
          <div className="wallet-modal-content">
            <div className="wallet-modal-header">
              <div className="wallet-modal-icon wallet-confirm-icon">⚠️</div>
              <h3 className="wallet-modal-title">Confirm {isBulkRequest ? "Bulk " : ""}Request</h3>
            </div>
            <div className="wallet-modal-body">
              <p className="wallet-modal-text">
                Send{" "}
                {isBulkRequest ? selectedTxIds.length + " items" : "this item"}
                {" "}worth{" "}
                <strong className="wallet-amount-highlight">
                  AED{" "}
                  {(isBulkRequest
                    ? selectedTotal
                    : walletData.transactions.find((t) => t._id === txToConfirm)
                        ?.amount || 0
                  ).toFixed(2)}
                </strong>{" "}
                to admin?
              </p>
            </div>
            <div className="wallet-modal-footer">
              <button className="wallet-btn wallet-btn-ghost" onClick={() => setShowConfirmModal(false)}>
                Cancel
              </button>
              <button className="wallet-btn wallet-btn-primary" onClick={confirmRequestPayToAdmin} disabled={payingTxId}>
                {payingTxId ? "Sending..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Wallet;
